import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  CheckIcon,
  XMarkIcon,
  ClockIcon,
  UserIcon,
  MapPinIcon,
  CurrencyRupeeIcon,
  CalendarIcon,
  ShoppingBagIcon,
  PhoneIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline'
import UnifiedOrderService from '../../services/unifiedOrderService'
import useAuthStore from '../../stores/useAuthStore'
import toast from 'react-hot-toast'

const OrdersTab = () => {
  const { user } = useAuthStore()
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [updatingOrder, setUpdatingOrder] = useState(null)
  const [selectedOrders, setSelectedOrders] = useState([])
  const [bulkUpdateMode, setBulkUpdateMode] = useState(false)
  const [bulkUpdating, setBulkUpdating] = useState(false)

  // Fetch orders on component mount
  useEffect(() => {
    if (user?.id) {
      fetchOrders()
    }
  }, [user?.id])

  const fetchOrders = async () => {
    try {
      setLoading(true)
      const fetchedOrders = await UnifiedOrderService.getRestaurantOrders(user.id)
      setOrders(fetchedOrders || [])
    } catch (error) {
      console.error('❌ Error fetching orders:', error)
      toast.error('Failed to load orders')
      setOrders([])
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    try {
      setUpdatingOrder(orderId)
      await UnifiedOrderService.updateOrderStatus(orderId, newStatus, user.id, 'owner')
      await fetchOrders()
      toast.success(`Order ${newStatus} successfully`)
    } catch (error) {
      console.error('❌ Error updating order:', error)
      toast.error('Failed to update order status')
    } finally {
      setUpdatingOrder(null)
    }
  }

  const handleUpdatePaymentStatus = async (orderId, newPaymentStatus) => {
    try {
      setUpdatingOrder(orderId)
      await UnifiedOrderService.updatePaymentStatus(orderId, newPaymentStatus)
      await fetchOrders()
      toast.success(`Payment status updated to ${newPaymentStatus}`)
    } catch (error) {
      console.error('❌ Error updating payment status:', error)
      toast.error('Failed to update payment status')
    } finally {
      setUpdatingOrder(null)
    }
  }

  const handleBulkPaymentStatusUpdate = async (newPaymentStatus) => {
    if (selectedOrders.length === 0) {
      toast.error('Please select orders to update')
      return
    }
    try {
      setBulkUpdating(true)
      const updatePromises = selectedOrders.map(orderId => 
        UnifiedOrderService.updatePaymentStatus(orderId, newPaymentStatus)
      )
      await Promise.all(updatePromises)
      await fetchOrders()
      toast.success(`Updated ${selectedOrders.length} order(s) to ${newPaymentStatus}`)
      setSelectedOrders([])
      setBulkUpdateMode(false)
    } catch (error) {
      console.error('❌ Error bulk updating payment status:', error)
      toast.error('Failed to update some orders')
    } finally {
      setBulkUpdating(false)
    }
  }

  const toggleOrderSelection = (orderId) => {
    setSelectedOrders(prev => {
      if (prev.includes(orderId)) {
        return prev.filter(id => id !== orderId)
      } else {
        return [...prev, orderId]
      }
    })
  }

  const selectAllPendingPayments = () => {
    const pendingOrders = filteredOrders
      .filter(order => order.payment_status === 'pending')
      .map(order => order.id)
    setSelectedOrders(pendingOrders)
    setBulkUpdateMode(true)
    toast.success(`Selected ${pendingOrders.length} pending payment order(s)`)
  }

  const selectAllOrders = () => {
    const allOrderIds = filteredOrders.map(order => order.id)
    setSelectedOrders(allOrderIds)
    setBulkUpdateMode(true)
    toast.success(`Selected ${allOrderIds.length} order(s)`)
  }

  const clearSelection = () => {
    setSelectedOrders([])
    setBulkUpdateMode(false)
  }

  const filteredOrders = orders.filter(order => {
    const customerName = order.customers?.full_name || order.customer_name || ''
    const matchesSearch = order.order_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         customerName.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter
    return matchesSearch && matchesStatus
  })

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-amber-50 text-amber-600 border-amber-100'
      case 'assigned': return 'bg-blue-50 text-blue-600 border-blue-100'
      case 'preparing': return 'bg-indigo-50 text-indigo-600 border-indigo-100'
      case 'ready': return 'bg-emerald-50 text-emerald-600 border-emerald-100'
      case 'delivered': return 'bg-gray-100 text-gray-600 border-gray-200'
      case 'cancelled': return 'bg-rose-50 text-rose-600 border-rose-100'
      default: return 'bg-gray-50 text-gray-500 border-gray-100'
    }
  }

  const getPaymentStatusColor = (paymentStatus) => {
    switch (paymentStatus) {
      case 'completed': return 'bg-emerald-50 text-emerald-600 border-emerald-100'
      case 'pending': return 'bg-amber-50 text-amber-600 border-amber-100'
      case 'failed': return 'bg-rose-50 text-rose-600 border-rose-100'
      case 'refunded': return 'bg-purple-50 text-purple-600 border-purple-100'
      default: return 'bg-gray-50 text-gray-500 border-gray-100'
    }
  }

  const getNextStatus = (currentStatus) => {
    const statusFlow = {
      'pending': 'assigned',
      'assigned': 'preparing',
      'preparing': 'ready',
      'ready': 'delivered'
    }
    return statusFlow[currentStatus]
  }

  return (
    <div className="space-y-12">
      {/* Search & Filter Ecosystem */}
      <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-sm transition-all duration-500 hover:shadow-xl hover:shadow-gray-200/20">
        <div className="flex flex-col gap-8">
          <div className="flex flex-col xl:flex-row gap-6 xl:items-center xl:justify-between">
            <div>
               <h2 className="text-2xl font-black text-gray-900 tracking-tight uppercase tracking-widest">Live Orders</h2>
               <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mt-1">Manage and track your active orders</p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
              {/* Refined Search */}
              <div className="relative group flex-1 sm:flex-none">
                <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 group-hover:text-orange-500 transition-colors" />
                <input
                  type="text"
                  placeholder="Order ID, Customer, Table..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-11 pr-6 py-3.5 bg-gray-50 border-gray-100 rounded-2xl focus:ring-4 focus:ring-orange-100 focus:bg-white text-sm font-bold placeholder:text-gray-300 transition-all w-full sm:w-80"
                />
              </div>
              
              {/* Premium Status Filter */}
              <div className="relative group">
                <FunnelIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 group-hover:text-orange-500 transition-colors pointer-events-none" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="pl-11 pr-10 py-3.5 bg-gray-50 border-gray-100 rounded-2xl focus:ring-4 focus:ring-orange-100 focus:bg-white text-sm font-black uppercase tracking-widest appearance-none transition-all cursor-pointer w-full sm:w-auto"
                >
                  <option value="all">ALL STATUS</option>
                  <option value="pending">PENDING</option>
                  <option value="assigned">ASSIGNED</option>
                  <option value="preparing">PREPARING</option>
                  <option value="ready">READY</option>
                  <option value="delivered">DELIVERED</option>
                  <option value="cancelled">CANCELLED</option>
                </select>
                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                   <ChevronDownIcon className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
                </div>
              </div>
            </div>
          </div>

          {/* Bulk Action Center */}
          <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between border-t border-gray-50 pt-8">
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setBulkUpdateMode(!bulkUpdateMode)}
                className={`flex items-center gap-2 px-6 py-3 rounded-2xl text-xs font-black uppercase tracking-widest transition-all duration-300 ${
                  bulkUpdateMode 
                    ? 'bg-orange-500 text-white shadow-lg shadow-orange-200 ring-4 ring-orange-50' 
                    : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                }`}
              >
                {bulkUpdateMode ? '✓ Exit Bulk Edit' : 'Bulk Edit'}
              </button>
              
              {bulkUpdateMode && (
                <div className="flex gap-2 animate-in slide-in-from-left duration-500">
                  <button
                    onClick={selectAllPendingPayments}
                    className="px-4 py-3 bg-white border border-gray-100 text-gray-700 rounded-2xl text-xs font-black uppercase tracking-widest hover:border-amber-200 hover:bg-amber-50 transition-all"
                  >
                    Select Pending ({filteredOrders.filter(o => o.payment_status === 'pending').length})
                  </button>
                  
                  <button
                    onClick={selectAllOrders}
                    className="px-4 py-3 bg-white border border-gray-100 text-gray-700 rounded-2xl text-xs font-black uppercase tracking-widest hover:border-blue-200 hover:bg-blue-50 transition-all"
                  >
                    Select All ({filteredOrders.length})
                  </button>
                  
                  {selectedOrders.length > 0 && (
                    <button
                      onClick={clearSelection}
                      className="px-4 py-3 bg-rose-50 text-rose-500 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-rose-100 transition-all"
                    >
                      Reset
                    </button>
                  )}
                </div>
              )}
            </div>

            {selectedOrders.length > 0 && (
              <div className="flex items-center gap-6 bg-orange-50/50 p-2 pl-6 rounded-full border border-orange-100 animate-in zoom-in duration-300 w-full lg:w-auto">
                <span className="text-xs font-black text-orange-900 uppercase tracking-tighter">
                  {selectedOrders.length} SELECTED
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleBulkPaymentStatusUpdate('completed')}
                    disabled={bulkUpdating}
                    className="px-4 py-2 bg-emerald-500 text-white rounded-full text-[10px] font-black uppercase tracking-widest hover:bg-emerald-600 shadow-sm transition-all"
                  >
                    Mark Paid
                  </button>
                  <button
                    onClick={() => handleBulkPaymentStatusUpdate('failed')}
                    disabled={bulkUpdating}
                    className="px-4 py-2 bg-rose-500 text-white rounded-full text-[10px] font-black uppercase tracking-widest hover:bg-rose-600 shadow-sm transition-all"
                  >
                    Mark Failed
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Loading & Orders Grid */}
      {loading ? (
        <div className="bg-white rounded-[32px] p-20 border border-gray-100 text-center shadow-sm">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-6"></div>
          <p className="text-gray-400 font-bold uppercase tracking-widest text-xs">Fetching latest orders...</p>
        </div>
      ) : (
        <div className="grid gap-8">
          {filteredOrders.length > 0 ? filteredOrders.map((order, index) => (
            <motion.div
              key={order.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05, type: "spring", damping: 20 }}
              className={`bg-white rounded-[32px] p-8 border transition-all duration-300 relative overflow-hidden group ${
                selectedOrders.includes(order.id) 
                  ? 'border-orange-500 shadow-2xl shadow-orange-100 ring-4 ring-orange-50' 
                  : 'border-gray-100 hover:border-gray-200 hover:shadow-xl hover:shadow-gray-200/20'
              }`}
            >
              {/* Selection Checkbox Overlay */}
              {bulkUpdateMode && (
                <div className="absolute top-6 left-6 z-10">
                  <input
                    type="checkbox"
                    checked={selectedOrders.includes(order.id)}
                    onChange={() => toggleOrderSelection(order.id)}
                    className="h-6 w-6 text-orange-600 border-2 border-gray-200 rounded-lg focus:ring-orange-500 cursor-pointer accent-orange-500 transition-all"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              )}

              <div className={`flex flex-col xl:flex-row gap-10 ${bulkUpdateMode ? 'pl-10' : ''}`}>
                {/* Order Identity & Core Stats */}
                <div className="xl:w-80 space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-gray-900 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-gray-200">
                      <span className="text-lg font-black tracking-tighter">#{order.order_number.slice(-3)}</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-black text-gray-900 tracking-tight">#{order.order_number}</h3>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Received {new Date(order.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <span className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border ${getStatusColor(order.status)}`}>
                        {order.status}
                    </span>
                    <span className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border ${getPaymentStatusColor(order.payment_status)}`}>
                        {order.payment_status || 'PENDING'}
                    </span>
                  </div>

                  {/* Amount Display */}
                  <div className="p-4 bg-gray-50 rounded-2xl inline-block border border-gray-100">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-none mb-2">Total Amount</p>
                    <p className="text-2xl font-black text-gray-900 leading-none tracking-tight">₹{order.total_amount}</p>
                  </div>
                </div>

                {/* Detail Blocks */}
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-8 border-l border-gray-50 xl:pl-10">
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center flex-shrink-0">
                          <UserIcon className="h-5 w-5 text-orange-500" />
                        </div>
                        <div>
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Customer</p>
                          <p className="text-sm font-black text-gray-900">{order.customers?.full_name || order.customer_name || 'Guest'}</p>
                          {order.customers?.phone && <p className="text-xs text-gray-400 font-bold mt-1 tracking-tight">{order.customers.phone}</p>}
                        </div>
                    </div>

                    <div className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                          <MapPinIcon className="h-5 w-5 text-blue-500" />
                        </div>
                        <div>
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Table Info</p>
                          <p className="text-sm font-black text-blue-600">
                              {order.tables?.table_number ? `Table ${order.tables.table_number}` : 'No Table'}
                          </p>
                          {order.tables?.location && <p className="text-xs text-gray-400 font-bold mt-1">{order.tables.location}</p>}
                        </div>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center flex-shrink-0">
                          <CheckIcon className="h-5 w-5 text-indigo-500" />
                        </div>
                        <div>
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Handled By</p>
                          <p className="text-sm font-black text-gray-900">
                              {order.assigned_staff?.full_name || order.users?.full_name || 'System Auto'}
                          </p>
                          <span className="text-[10px] font-bold text-gray-300 uppercase italic">Assigned Staff</span>
                        </div>
                    </div>

                    <div className="flex flex-col gap-2">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Update Payment Status</p>
                        <div className="relative group/sel">
                          <select
                            value={order.payment_status || 'pending'}
                            onChange={(e) => handleUpdatePaymentStatus(order.id, e.target.value)}
                            disabled={updatingOrder === order.id}
                            className="text-[10px] font-black uppercase tracking-widest w-full px-4 py-2 bg-gray-50 border-gray-100 rounded-xl focus:ring-2 focus:ring-orange-100 appearance-none cursor-pointer transition-all"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <option value="pending">Mark Pending</option>
                            <option value="completed">Mark Paid</option>
                            <option value="failed">Mark Failed</option>
                            <option value="refunded">Mark Refunded</option>
                          </select>
                          <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400 pointer-events-none" />
                        </div>
                    </div>
                  </div>
                </div>

                {/* Action Sidebar */}
                <div className="xl:w-48 flex flex-row xl:flex-col gap-3">
                  <button
                    onClick={() => setSelectedOrder(order)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-50 text-gray-600 rounded-2xl hover:bg-gray-100 transition-all font-black text-[10px] uppercase tracking-widest"
                  >
                    <EyeIcon className="h-4 w-4" />
                    View
                  </button>
                  
                  {order.status !== 'delivered' && order.status !== 'cancelled' && order.status !== 'completed' && (
                    <button
                      onClick={() => handleUpdateOrderStatus(order.id, getNextStatus(order.status))}
                      disabled={updatingOrder === order.id}
                      className="flex-[2] xl:flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-2xl hover:bg-black disabled:bg-gray-200 shadow-lg shadow-gray-200 transition-all font-black text-[10px] uppercase tracking-widest"
                    >
                      {updatingOrder === order.id ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      ) : (
                        <CheckIcon className="h-4 w-4" />
                      )}
                      {getNextStatus(order.status) === 'assigned' && 'Assign Staff'}
                      {getNextStatus(order.status) === 'preparing' && 'Start Cooking'}
                      {getNextStatus(order.status) === 'ready' && 'Ready'}
                      {getNextStatus(order.status) === 'delivered' && 'Dispatch'}
                    </button>
                  )}
                  
                  {order.status === 'pending' && (
                    <button
                      onClick={() => handleUpdateOrderStatus(order.id, 'cancelled')}
                      disabled={updatingOrder === order.id}
                      className="p-3 text-rose-500 hover:bg-rose-50 rounded-2xl transition-colors"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Progress Bar */}
              <div className="absolute bottom-0 left-0 h-1 bg-gray-50 w-full">
                <motion.div 
                    initial={{ width: 0 }}
                    animate={{ 
                      width: order.status === 'delivered' ? '100%' : 
                            order.status === 'ready' ? '75%' :
                            order.status === 'preparing' ? '50%' :
                            order.status === 'assigned' ? '25%' : '10%'
                    }}
                    className={`h-full transition-all duration-1000 ${
                      order.status === 'ready' ? 'bg-emerald-500' : 'bg-orange-500'
                    }`}
                />
              </div>
            </motion.div>
          )) : (
            <div className="bg-white rounded-[40px] p-20 border border-gray-100 text-center shadow-sm">
              <ShoppingBagIcon className="h-10 w-10 text-gray-300 mx-auto mb-8" />
              <h3 className="text-2xl font-black text-gray-900 mb-3">No hungry customers yet</h3>
              <p className="text-gray-400 font-bold max-w-sm mx-auto text-sm">
                The dashboard will come alive once orders start rolling in. Keep your kitchen ready!
              </p>
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {selectedOrder && (
          <>
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]" 
              onClick={() => setSelectedOrder(null)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed inset-4 md:inset-10 lg:inset-20 bg-white rounded-[48px] shadow-2xl z-[110] overflow-hidden border border-gray-100 flex flex-col"
            >
              <div className="bg-white px-12 py-10 flex items-center justify-between border-b border-gray-50 flex-shrink-0">
                <div className="flex items-center space-x-6">
                  <div className="w-16 h-16 bg-gray-900 rounded-[20px] flex items-center justify-center text-white">
                    <ShoppingBagIcon className="h-8 w-8" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-black text-gray-900">Order #{selectedOrder.order_number}</h2>
                    <div className="flex gap-3 mt-1">
                      <span className={`px-4 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${getStatusColor(selectedOrder.status)}`}>
                        {selectedOrder.status}
                      </span>
                    </div>
                  </div>
                </div>
                <button onClick={() => setSelectedOrder(null)} className="w-14 h-14 bg-gray-50 rounded-full flex items-center justify-center">
                  <XMarkIcon className="h-6 w-6 text-gray-400" />
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto px-12 py-10">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                  <div className="space-y-10">
                    <section>
                      <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-6">Customer</h4>
                      <div className="p-8 bg-gray-50 rounded-[32px]">
                        <p className="text-xl font-black text-gray-900">{selectedOrder.customers?.full_name || selectedOrder.customer_name || 'Guest'}</p>
                        <p className="text-sm font-bold text-gray-400 mt-2">{selectedOrder.customers?.phone || 'No phone'}</p>
                      </div>
                    </section>
                    <section>
                      <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-6">Table Info</h4>
                      <div className="p-8 bg-blue-50/50 rounded-[32px] border border-blue-100 text-blue-700">
                        <p className="text-xl font-black">{selectedOrder.tables?.table_number ? `Table ${selectedOrder.tables.table_number}` : 'No Table'}</p>
                      </div>
                    </section>
                  </div>
                  
                  <div className="lg:col-span-2 space-y-10">
                    <section>
                      <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-6">Order Items</h4>
                      <div className="bg-white rounded-[40px] border border-gray-100 overflow-hidden">
                        {selectedOrder.order_items?.map((item, idx) => (
                          <div key={idx} className="p-8 flex items-center justify-between border-b border-gray-50 last:border-0">
                            <div className="flex gap-6">
                              <div className="w-12 h-12 bg-gray-50 rounded-2xl flex items-center justify-center font-black text-gray-900">{item.quantity}x</div>
                              <p className="text-base font-black text-gray-900">{item.item_name}</p>
                            </div>
                            <p className="text-lg font-black text-gray-900">₹{item.total_price}</p>
                          </div>
                        ))}
                        <div className="p-8 bg-gray-900 text-white flex items-center justify-between">
                          <p className="text-xs font-bold uppercase tracking-widest opacity-50">Total Amount</p>
                          <p className="text-4xl font-black">₹{selectedOrder.total_amount}</p>
                        </div>
                      </div>
                    </section>
                  </div>
                </div>
              </div>

              <div className="px-12 py-10 bg-gray-50 border-t border-gray-100 flex justify-end gap-6">
                <button onClick={() => setSelectedOrder(null)} className="px-10 py-5 bg-white text-gray-900 border border-gray-200 rounded-[24px] font-black text-xs uppercase tracking-widest">
                  Dismiss
                </button>
                {selectedOrder.status !== 'delivered' && selectedOrder.status !== 'cancelled' && (
                  <button 
                    onClick={() => {
                      handleUpdateOrderStatus(selectedOrder.id, getNextStatus(selectedOrder.status))
                      setSelectedOrder(null)
                    }}
                    className="px-12 py-5 bg-orange-500 text-white rounded-[24px] font-black text-xs uppercase tracking-widest"
                  >
                    Next Step
                  </button>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}

export default OrdersTab
